<template>
  <div class="container-fluid mt-3">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h2>Ecolabel Management</h2>
        <p class="text-muted mb-0">
          Export and import RegistryDevice data for ecolabel certifications
        </p>
      </div>
      <button class="btn btn-success" @click="openUploadModal">
        <i class="fas fa-upload me-2"></i>
        Upload Ecolabeled Devices
      </button>
    </div>

    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">Export Filters</h5>
      </div>
      <div class="card-body">
        <!-- Country Filters -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Countries</h6>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary me-2"
                @click="selectAllCountries"
              >
                Select All
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="deselectAllCountries"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div class="row">
            <div
              v-for="country in availableCountries"
              :key="country"
              class="col-md-3 col-sm-4 col-6 mb-2"
            >
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`country-${country}`"
                  :value="country"
                  v-model="selectedCountries"
                  @change="updatePreview"
                />
                <label class="form-check-label" :for="`country-${country}`">
                  {{ getCountryLabel(country) }}
                </label>
              </div>
            </div>
          </div>
          <div v-if="selectedCountries.length === 0" class="text-danger small">
            Please select at least one country
          </div>
        </div>

        <!-- Technology Filters -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Technology Types</h6>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary me-2"
                @click="selectAllTechnologies"
              >
                Select All
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="deselectAllTechnologies"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div class="row">
            <div
              v-for="tech in technologyTypes"
              :key="tech"
              class="col-md-3 col-sm-4 col-6 mb-2"
            >
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  :id="`tech-${tech}`"
                  :value="tech"
                  v-model="selectedTechnologies"
                  @change="updatePreview"
                />
                <label class="form-check-label" :for="`tech-${tech}`">
                  {{ tech }}
                </label>
              </div>
            </div>
          </div>
          <div
            v-if="selectedTechnologies.length === 0"
            class="text-danger small"
          >
            Please select at least one technology type
          </div>
        </div>

        <!-- Additional Options -->
        <div class="mb-4">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Additional Options</h6>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary me-2"
                @click="selectAllOptions"
              >
                Select All
              </button>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="deselectAllOptions"
              >
                Deselect All
              </button>
            </div>
          </div>
          <div class="form-check mb-2">
            <input
              class="form-check-input"
              type="checkbox"
              id="excludeEcolabeled"
              v-model="excludeEcolabeled"
              @change="updatePreview"
            />
            <label class="form-check-label" for="excludeEcolabeled">
              Exclude already ecolabeled devices
            </label>
          </div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="checkbox"
              id="excludeNoAddress"
              v-model="excludeNoAddress"
              @change="updatePreview"
            />
            <label class="form-check-label" for="excludeNoAddress">
              Exclude devices without address
            </label>
          </div>
        </div>

        <!-- Preview Section -->
        <div class="alert" :class="previewAlertClass">
          <div v-if="previewLoading">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Loading device count...
          </div>
          <div v-else-if="previewError">
            <i class="fas fa-exclamation-triangle me-2"></i>
            Error loading preview: {{ previewError }}
          </div>
          <div v-else-if="!canExport">
            <i class="fas fa-info-circle me-2"></i>
            Please select at least one country and one technology type to see
            preview
          </div>
          <div v-else>
            <strong>{{ deviceCount }}</strong>
            {{ deviceCount === 1 ? "device" : "devices" }} match the current
            filter criteria
          </div>
        </div>

        <!-- Export Button -->
        <div class="d-flex justify-content-end">
          <button
            class="btn btn-primary"
            :disabled="!canExport || deviceCount === 0 || downloadLoading"
            @click="downloadCSV"
          >
            <span
              v-if="downloadLoading"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <i v-else class="fas fa-download me-2"></i>
            Download CSV Export
          </button>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <ModalComponent
      ref="uploadModal"
      submitButtonText="Upload CSV"
      :submit="handleUpload"
      :submitDisabled="!selectedFile || uploadLoading"
      @close="handleModalClose"
    >
      <template #title>Upload Ecolabeled Devices</template>
      <template #body>
        <div>
          <p class="text-muted mb-3">
            Upload a CSV file with device IDs to mark as ecolabeled. The CSV
            must contain an 'internal_id' column matching the format from the
            export.
          </p>

          <!-- File Input -->
          <div class="mb-3">
            <label for="csvFileInput" class="form-label">Select CSV File</label>
            <input
              id="csvFileInput"
              ref="fileInput"
              type="file"
              class="form-control"
              accept=".csv"
              @change="handleFileSelect"
              :disabled="uploadLoading"
            />
          </div>

          <!-- Upload Status -->
          <div v-if="uploadLoading" class="alert alert-info">
            <span class="spinner-border spinner-border-sm me-2"></span>
            Processing upload...
          </div>

          <!-- Success Message -->
          <div
            v-if="uploadResult && uploadResult.success"
            class="alert alert-success"
          >
            <i class="fas fa-check-circle me-2"></i>
            Successfully updated {{ uploadResult.updated_count }}
            {{ uploadResult.updated_count === 1 ? "device" : "devices" }} as
            ecolabeled!
          </div>

          <!-- Error Messages -->
          <div
            v-if="
              uploadResult &&
              !uploadResult.success &&
              uploadResult.errors.length > 0
            "
            class="alert alert-danger"
          >
            <h6 class="alert-heading">
              <i class="fas fa-exclamation-triangle me-2"></i>
              Upload Failed - Please Fix These Errors:
            </h6>
            <hr />
            <div class="error-list" style="max-height: 300px; overflow-y: auto">
              <div
                v-for="error in uploadResult.errors"
                :key="`${error.row}-${error.device_id}`"
                class="mb-2"
              >
                <strong>Row {{ error.row }}:</strong> {{ error.error }}
              </div>
            </div>
            <hr />
            <p class="mb-0 small">
              No devices were updated. Please fix all errors and try again.
            </p>
          </div>
        </div>
      </template>
    </ModalComponent>
  </div>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { SUPPORTED_COUNTRIES_WITH_LABELS } from "@/constants";
import { TECHNOLOGY_KEYS } from "@/types/technology";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { debounce } from "lodash-es";
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Types
interface EcolabelUploadError {
  row: number;
  device_id: number;
  error: string;
}

interface EcolabelUploadResponse {
  success: boolean;
  updated_count: number;
  errors: EcolabelUploadError[];
}

// Injections
const { globalErrorHandler } = useGlobalHandlers();

// State
const availableCountries = ref<string[]>([]);
const selectedCountries = ref<string[]>([]);
const selectedTechnologies = ref<string[]>(["SOLAR", "WIND"]);
const excludeEcolabeled = ref(true);
const excludeNoAddress = ref(true);
const deviceCount = ref(0);
const previewLoading = ref(false);
const previewError = ref<string | null>(null);
const downloadLoading = ref(false);

// Upload state
const uploadModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("uploadModal");
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const selectedFile = ref<File | null>(null);
const uploadLoading = ref(false);
const uploadResult = ref<EcolabelUploadResponse | null>(null);

const technologyTypes = TECHNOLOGY_KEYS.map((key) => key.toUpperCase());

// Computed
const canExport = computed(
  () =>
    selectedCountries.value.length > 0 && selectedTechnologies.value.length > 0
);

const previewAlertClass = computed(() => {
  if (previewError.value) return "alert-danger";
  if (!canExport.value) return "alert-warning";
  if (deviceCount.value === 0) return "alert-warning";
  return "alert-info";
});

// Methods
const getCountryLabel = (countryCode: string): string => {
  const country = SUPPORTED_COUNTRIES_WITH_LABELS.find(
    (c) => c.value === countryCode
  );
  return country ? country.label : countryCode;
};

const fetchAvailableCountries = async () => {
  try {
    const response = await goSolidApi.get<{ countries: string[] }>(
      "/api/admin/registry-devices/countries"
    );
    availableCountries.value = response.data.countries;
    // Select all countries by default
    selectedCountries.value = [...response.data.countries];
    // Trigger initial preview update
    updatePreview();
  } catch (error) {
    console.error("Failed to fetch available countries:", error);
    previewError.value = "Failed to load available countries";
  }
};

const fetchDeviceCount = async () => {
  if (!canExport.value) {
    deviceCount.value = 0;
    return;
  }

  previewLoading.value = true;
  previewError.value = null;

  try {
    const response = await goSolidApi.post<{ count: number }>(
      "/api/admin/registry-devices/ecolabel-export/preview",
      {
        countries: selectedCountries.value,
        technologies: selectedTechnologies.value,
        exclude_ecolabeled: excludeEcolabeled.value,
        exclude_no_address: excludeNoAddress.value,
      }
    );
    deviceCount.value = response.data.count;
  } catch (error: any) {
    console.error("Failed to fetch device count:", error);
    previewError.value =
      error.response?.data?.message || "Failed to load device count";
  } finally {
    previewLoading.value = false;
  }
};

const debouncedFetchDeviceCount = debounce(fetchDeviceCount, 500);

const updatePreview = () => {
  debouncedFetchDeviceCount();
};

// Select/Deselect methods
const selectAllCountries = () => {
  selectedCountries.value = [...availableCountries.value];
  updatePreview();
};

const deselectAllCountries = () => {
  selectedCountries.value = [];
  updatePreview();
};

const selectAllTechnologies = () => {
  selectedTechnologies.value = [...technologyTypes];
  updatePreview();
};

const deselectAllTechnologies = () => {
  selectedTechnologies.value = [];
  updatePreview();
};

const selectAllOptions = () => {
  excludeEcolabeled.value = true;
  excludeNoAddress.value = true;
  updatePreview();
};

const deselectAllOptions = () => {
  excludeEcolabeled.value = false;
  excludeNoAddress.value = false;
  updatePreview();
};

const downloadCSV = async () => {
  if (!canExport.value || deviceCount.value === 0) {
    return;
  }

  downloadLoading.value = true;

  try {
    const response = await goSolidApi.post(
      "/api/admin/registry-devices/ecolabel-export/download",
      {
        countries: selectedCountries.value,
        technologies: selectedTechnologies.value,
        exclude_ecolabeled: excludeEcolabeled.value,
        exclude_no_address: excludeNoAddress.value,
      },
      {
        responseType: "blob",
      }
    );

    const fallbackFilename = `ecolabel-export-${
      new Date().toISOString().split("T")[0]
    }.csv`;

    downloadFromApiResponse(
      response.data,
      response.headers,
      fallbackFilename,
      "text/csv"
    );
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    downloadLoading.value = false;
  }
};

const openUploadModal = () => {
  uploadModal.value?.toggleModal();
  // Reset state when opening modal
  selectedFile.value = null;
  uploadResult.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

const handleFileSelect = (event: Event) => {
  const target = event.target as HTMLInputElement;
  selectedFile.value = target.files?.[0] ?? null;
  // Clear previous results when selecting a new file
  uploadResult.value = null;
};

const handleUpload = async () => {
  if (!selectedFile.value) {
    return;
  }

  uploadLoading.value = true;
  uploadResult.value = null;

  try {
    const formData = new FormData();
    formData.append("file", selectedFile.value);

    const response = await goSolidApi.post<EcolabelUploadResponse>(
      "/api/admin/registry-devices/ecolabel-upload",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    uploadResult.value = response.data;

    // If successful, refresh the preview count to reflect the changes
    if (response.data.success) {
      updatePreview();
    }
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    uploadLoading.value = false;
  }
};

const handleModalClose = () => {
  // Reset state when closing modal
  selectedFile.value = null;
  uploadResult.value = null;
  if (fileInput.value) {
    fileInput.value.value = "";
  }
};

// Lifecycle
onMounted(() => {
  fetchAvailableCountries();
});
</script>

<style scoped lang="scss">
.form-check-input {
  cursor: pointer;
}

.form-check-label {
  cursor: pointer;
  user-select: none;
}
</style>
